<template>
  <div
    v-tooltip.top="
      !isAssistantConfigured
        ? 'The AI model is not configured. Please click to configure it in settings.'
        : null
    "
    class="relative group"
  >
    <button
      :disabled="isLoading"
      class="group/btn flex h-8 items-center justify-center rounded-md border border-blue-800 bg-indigo-50 p-1.5 text-blue-800 transition-all duration-300 hover:bg-indigo-50 disabled:cursor-not-allowed"
      @click="handleClick"
    >
      <div v-if="isLoading" class="flex h-4 w-4 shrink-0 items-center justify-center space-x-0.5">
        <div
          class="h-1 w-1 animate-bounce rounded-full bg-current"
          style="animation-delay: 0ms"
        ></div>
        <div
          class="h-1 w-1 animate-bounce rounded-full bg-current"
          style="animation-delay: 150ms"
        ></div>
        <div
          class="h-1 w-1 animate-bounce rounded-full bg-current"
          style="animation-delay: 300ms"
        ></div>
      </div>
      <SparklesIcon v-else class="h-4 w-4 shrink-0 fill-current" />
      <span
        class="max-w-0 overflow-hidden whitespace-nowrap pl-0 text-sm font-medium opacity-0 transition-all duration-300 group-hover/btn:max-w-[200px] group-hover/btn:pl-1.5 group-hover/btn:opacity-100"
      >
        Ai extraction
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { ref } from "vue";
import { ReviewKey } from "../../../use-review";
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import useSnackbar from "@app/composables/use-snackbar";
import useAiAssistant from "@app/composables/useAiAssistant";
import { SparklesIcon } from "@heroicons/vue/24/outline";
import { useRouter } from "vue-router";
const props = withDefaults(
  defineProps<{
    study: ReviewItem;
    attributeStructure: AttributeStructure;
    query: string;
  }>(),
  {
    query: "",
  },
);

const snackbar = useSnackbar();
const { isConfigured: isAssistantConfigured } = useAiAssistant();

const isGeneratingAnswer = defineModel<boolean>("isGeneratingAnswer", {
  default: false,
});

const emit = defineEmits<{
  aiCompleted: [];
}>();

const review = injectStrict(ReviewKey);
const isLoading = ref(false);
const router = useRouter();

async function generateAnswer() {
  const attributeId = props.attributeStructure.id;
  if (!attributeId) {
    return;
  }
  isLoading.value = true;
  isGeneratingAnswer.value = true;
  try {
    await review.answerStudyQuestion(props.study.id, attributeId);
    snackbar.success("Success");
    emit("aiCompleted");
  } catch (_) {
    snackbar.error("The AI model timed out. Please try again.");
  } finally {
    isLoading.value = false;
    isGeneratingAnswer.value = false;
  }
}

function handleClick() {
  if (isAssistantConfigured.value) {
    generateAnswer();
  } else {
    router.push("/settings");
  }
}
</script>
